import React, { useEffect, useState, useMemo } from "react";
import DialogContent from "@mui/material/DialogContent";
import { IFlight, IGroundEvent, IBulkOpsState, BulkActionType, IBulkEventPayload } from "@vmosolutions/oc-interfaces";
import { bulkEvent } from "src/redux/actions/flights/FlightData.Actions";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";

import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, Grid, LinearProgress, useTheme } from "@mui/material";
import { EventModalTypes } from "../DialogModalWrapper";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalMessage, ModalButton } from "../DialogModalComponents";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { DialogModalStyles } from "../DialogModalWrapper";
// Remove open editor windows if events are deleted
import { removeEditFlightWindow } from "src/redux/actions/flights/FlightTimeline.Actions";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";

/**
 * @param isOpen - to keep track of whither the modal should be open or closed
 * @param handleClose - sets isOpen to true or false in DialogModalWrapper.tsx
 * @param setActiveFlightModal - sets the  state of activeFlightModal in FlightTimelineWrapper.tsx
 * @returns the modal
 */

interface IDeleteEventModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

export const DeleteEventModal = (props: IDeleteEventModalProps): JSX.Element => {
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  // For BULK batch tracking
  const [batchId, setBatchId] = useState<any>(null);
  const batchJob: IBulkOpsState = useSelector((state: IRootState) => state.bulkBatch[batchId]);
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const selectedGroundEvents: IGroundEvent[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => !isFlight(event)) as IGroundEvent[];
  }, [selectedEvents]);

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  //want these variables to stay the same
  const [typeFlight] = useState<boolean>(selectedFlights.length > 0 ? true : false);
  const [groundEventPluralFormat] = useState<string>(
    selectedGroundEvents.length > 1 ? "Ground Events" : "Ground Event"
  );
  const [flightPluralFormat] = useState<string>(selectedFlights.length > 1 ? "Flights" : "Flight");
  const dispatch = useDispatch();
  const theme = useTheme();

  useEffect(() => {
    if (batchJob) {
      const eventTypes = typeFlight ? "flights" : "ground events";
      const { records, successfulEventIds, failedEvents } = batchJob;
      const failedUpdates = failedEvents?.length > 0 ? `, ${failedEvents?.length} ${eventTypes} failed to delete` : "";
      setBuildStatus(`${successfulEventIds?.length} of ${records} ${eventTypes} successfully deleted${failedUpdates}`);
      // Build completed
      if (successfulEventIds?.length + failedEvents?.length === records) {
        const failedEvents = typeFlight
          ? selectedFlights.filter((flight) => !successfulEventIds.map((m) => m.objectId).includes(flight._id))
          : selectedGroundEvents.filter((ge) => !successfulEventIds.map((m) => m.objectId).includes(ge._id));
        dispatch(setSelectedEvents(failedEvents));
        setBuilding(false);
      }
      if (successfulEventIds?.length === records) {
        props.handleClose(false);
        props.setActiveEventModal(EventModalTypes.None);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJob, dispatch]);

  const bulkProgress: number = useMemo(() => {
    if (!batchJob) {
      return 0;
    }
    const totalCount = batchJob.records;
    const completed = batchJob?.successfulEventIds?.length ?? 0;
    const failed = batchJob?.failedEvents?.length ?? 0;
    const percentageComplete = totalCount === 0 ? 0 : ((completed + failed) * 100) / totalCount;
    return percentageComplete;
  }, [batchJob]);

  //////////////
  // HANDLERS //
  //////////////

  const getTitle = (): string => {
    if (typeFlight) {
      return `Delete ${flightPluralFormat}`;
    } else {
      return `Delete ${groundEventPluralFormat}`;
    }
  };

  const getMessage = (): string => {
    if (typeFlight) {
      return `Are you sure you want to delete the selected ${flightPluralFormat.toLowerCase()}?`;
    } else {
      return `Are you sure you want to delete the selected ${groundEventPluralFormat.toLowerCase()}?`;
    }
  };

  const handleCancel = (): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    props.handleClose(false);
    props.setActiveEventModal(EventModalTypes.None);
  };

  const handleConfirm = async (): Promise<void> => {
    if (typeFlight) {
      // FLIGHTS

      setBuilding(true);
      setBuildStatus("Deleting selected flights");
      const selectedFlightIds = selectedFlights.map((flight: IFlight) => {
        // Close any open editor windows associated with these ids
        dispatch(removeEditFlightWindow(flight._id));
        return flight._id;
      });
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.DELETE_FLIGHT,
        batchIds: selectedFlightIds,
        payload: null,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedFlights?.length));
      setBatchId(newBatchId);
    } else {
      // GROUND EVENTS

      setBuilding(true);
      setBuildStatus("Deleting selected ground events");
      const selectedEventIds = selectedGroundEvents.map((event: IGroundEvent) => {
        // Close any open editor windows associated with these ids
        dispatch(removeEditFlightWindow(event._id));
        return event._id;
      });
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.DELETE_GROUND_EVENT,
        batchIds: selectedEventIds,
        payload: null,
      };
      const newBatchId = await dispatch(bulkEvent(bulkPayload, selectedGroundEvents?.length));
      setBatchId(newBatchId);
    }
  };

  //////////////
  //  Return  //
  //////////////

  if (props.isOpen) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          {!!typeFlight && <FlightChipCollection flights={selectedFlights} disableContext={true} disableFocus={true} />}

          <ModalTitle title={getTitle()}></ModalTitle>

          <ModalMessage message={getMessage()}></ModalMessage>

          <DialogActions>
            <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
            <ModalButton label="Confirm" disable={building || !!buildStatus} handler={handleConfirm}></ModalButton>
          </DialogActions>

          <div className="modal_status">
            {!!buildStatus && (
              <>
                <Grid item style={{ margin: "10px 0px" }}>
                  <LinearProgress variant="determinate" value={bulkProgress} />
                </Grid>
                <LoadingMessage message={buildStatus} animate={building} />
              </>
            )}
          </div>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
